import type { LLChannelRow } from '@/types/lead-lifetime';
import { useMoney } from './currency';
import { CHANNEL_COLOR, CHANNEL_LABEL, fmtLL } from './format';

/**
 * De onde vem a receita dos sites no recorte: messageria, push, SMS, outros trackings e
 * "sem tracking" (impressão que chegou ao GAM sem `utm_source`). É isso que explica a
 * diferença entre este relatório e a receita total da dashboard.
 */
export function RevenueMix({ channels }: { channels: LLChannelRow[] }) {
    const money = useMoney();
    const rows = (channels || []).filter((c) => c.revenue > 0);
    const total = rows.reduce((sum, c) => sum + c.revenue, 0);
    if (!rows.length) return null;

    return (
        <div className="rounded-lg border border-border bg-card p-4 md:p-5">
            <div className="mb-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    De onde vem a receita
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                    Receita líquida dos domínios do recorte, por origem · “sem tracking” = impressão sem UTM no GAM
                </p>
            </div>

            <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
                {rows.map((c) => (
                    <div
                        key={c.channel}
                        title={`${CHANNEL_LABEL[c.channel] || c.channel}: ${money.fmt(c.revenue)}`}
                        style={{ width: `${((c.revenue / total) * 100).toFixed(2)}%`, backgroundColor: CHANNEL_COLOR[c.channel] || '#64748b' }}
                    />
                ))}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-5">
                {rows.map((c) => (
                    <div key={c.channel} className="flex items-start gap-2">
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: CHANNEL_COLOR[c.channel] || '#64748b' }} />
                        <div className="min-w-0">
                            <p className="truncate text-[11px] uppercase tracking-wider text-muted-foreground">
                                {CHANNEL_LABEL[c.channel] || c.channel}
                            </p>
                            <p className="tabular-nums text-sm font-semibold text-foreground">
                                {c.share == null ? '—' : fmtLL.pct(c.share)}
                            </p>
                            <p className="tabular-nums text-[11px] text-muted-foreground">{money.fmt(c.revenue)}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
